import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { CalendarIcon, MapPin, Clock, User, Phone, Car, CreditCard, Banknote, Loader2, Mail, Award } from 'lucide-react';
import RevolutPayment from './RevolutPayment';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// ============== SUB-COMPONENTS ==============

const LoyaltyBadge = ({ lang, isEligible }) => {
  if (!isEligible) return null;
  return (
    <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 p-3 mb-4 flex items-center gap-3" data-testid="loyalty-badge">
      <Award className="text-[#C5A059]" size={20} />
      <div>
        <p className="text-[#C5A059] text-sm font-medium">{lang === 'fr' ? 'Client fidèle' : 'Loyal Customer'}</p>
        <p className="text-white/60 text-xs">{lang === 'fr' ? '-10% appliqué automatiquement' : '-10% automatically applied'}</p>
      </div>
    </div>
  );
};

const ModeToggle = ({ mode, setMode, t, onModeChange }) => (
  <div className="flex mb-6 border border-white/10">
    <button type="button" onClick={() => { setMode('trip'); onModeChange(); }}
      className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${mode === 'trip' ? 'bg-[#C5A059] text-black' : 'bg-transparent text-white/70 hover:text-white'}`}
      data-testid="mode-trip-btn">
      {t('booking.tripMode')}
    </button>
    <button type="button" onClick={() => { setMode('hourly'); onModeChange(); }}
      className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${mode === 'hourly' ? 'bg-[#C5A059] text-black' : 'bg-transparent text-white/70 hover:text-white'}`}
      data-testid="mode-hourly-btn">
      {t('booking.hourlyMode')}
    </button>
  </div>
);

const AddressFields = ({ pickupRef, dropoffRef, pickupAddress, setPickupAddress, dropoffAddress, setDropoffAddress, manualDistance, setManualDistance, lang, t, isCalculating, onCalculate, canCalculate }) => (
  <>
    <div>
      <label className="label-luxury flex items-center gap-2"><MapPin size={12} />{t('booking.pickupLabel')}</label>
      <input ref={pickupRef} type="text" className="input-luxury" placeholder={t('booking.pickupPlaceholder')}
        value={pickupAddress} onChange={(e) => setPickupAddress(e.target.value)} data-testid="pickup-input" />
    </div>
    <div>
      <label className="label-luxury flex items-center gap-2"><MapPin size={12} />{t('booking.dropoffLabel')}</label>
      <input ref={dropoffRef} type="text" className="input-luxury" placeholder={t('booking.dropoffPlaceholder')}
        value={dropoffAddress} onChange={(e) => setDropoffAddress(e.target.value)} data-testid="dropoff-input" />
    </div>
    <div className="text-white/40 text-xs">
      {lang === 'fr' ? 'Distance calculée automatiquement ou entrez-la ci-dessous' : 'Distance calculated automatically or enter below'}
    </div>
    <div>
      <label className="label-luxury">{lang === 'fr' ? 'Distance (km) - optionnel' : 'Distance (km) - optional'}</label>
      <input type="number" className="input-luxury" placeholder="Ex: 25" value={manualDistance}
        onChange={(e) => setManualDistance(e.target.value)} min="1" step="0.1" data-testid="distance-input" />
    </div>
    <button type="button" onClick={onCalculate} disabled={isCalculating || !canCalculate}
      className="btn-secondary w-full flex items-center justify-center gap-2" data-testid="calculate-price-btn">
      {isCalculating ? <><Loader2 className="animate-spin" size={16} />{t('booking.calculating')}</> : t('booking.calculateBtn')}
    </button>
  </>
);

const HoursSelector = ({ hours, setHours, t }) => (
  <div>
    <label className="label-luxury flex items-center gap-2"><Clock size={12} />{t('booking.hoursLabel')}</label>
    <select value={hours} onChange={(e) => setHours(parseInt(e.target.value))} className="select-luxury" data-testid="hours-select">
      {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map(h => <option key={`hour-${h}`} value={h}>{h} {t('price.hours')}</option>)}
    </select>
  </div>
);

const PriceDisplay = ({ routeData, priceData, lang, t }) => {
  if (!routeData && !priceData) return null;
  return (
    <div className="bg-[#111111] border border-white/10 p-4 space-y-2" data-testid="price-display">
      {routeData && (
        <div className="flex justify-between text-sm text-white/70">
          <span>{t('price.distance')}: {routeData.distance_km.toFixed(1)} km</span>
          <span>{t('price.duration')}: ~{routeData.duration_minutes} {t('price.minutes')}</span>
        </div>
      )}
      {priceData && (
        <>
          <div className="flex justify-between text-sm">
            <span className="text-white/70">{t('price.basePrice')}</span>
            <span className="text-white">{priceData.base_price.toFixed(2)}€</span>
          </div>
          {priceData.night_surcharge > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-white/70">{t('price.nightSurcharge')}</span>
              <span className="text-[#C5A059]">+{priceData.night_surcharge.toFixed(2)}€</span>
            </div>
          )}
          {priceData.loyalty_discount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-white/70 flex items-center gap-1"><Award size={12} className="text-[#C5A059]" />{lang === 'fr' ? 'Remise fidélité (-10%)' : 'Loyalty (-10%)'}</span>
              <span className="text-green-400">-{priceData.loyalty_discount.toFixed(2)}€</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-semibold border-t border-white/10 pt-2 mt-2">
            <span className="text-white">{t('price.total')}</span>
            <span className="text-[#C5A059]">{priceData.total_price.toFixed(2)}€</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/50">{t('price.deposit')}</span>
            <span className="text-white/70">{priceData.deposit_amount.toFixed(2)}€</span>
          </div>
        </>
      )}
    </div>
  );
};

const VehicleSelector = ({ vehicle, setVehicle, t }) => (
  <div>
    <label className="label-luxury flex items-center gap-2"><Car size={12} />{t('booking.vehicleLabel')}</label>
    <div className="grid grid-cols-2 gap-3">
      <button type="button" onClick={() => setVehicle('model_y')} className={`vehicle-card ${vehicle === 'model_y' ? 'selected' : ''}`} data-testid="vehicle-model-y">
        <div className="text-sm font-medium text-white">{t('vehicles.modelY.name')}</div>
        <div className="text-xs text-white/50 mt-1">{t('vehicles.modelY.description')}</div>
      </button>
      <button type="button" onClick={() => setVehicle('model_3')} className={`vehicle-card ${vehicle === 'model_3' ? 'selected' : ''}`} data-testid="vehicle-model-3">
        <div className="text-sm font-medium text-white">{t('vehicles.model3.name')}</div>
        <div className="text-xs text-white/50 mt-1">{t('vehicles.model3.description')}</div>
      </button>
    </div>
  </div>
);

const PaymentMethodSelector = ({ paymentMethod, setPaymentMethod, t }) => (
  <div>
    <label className="label-luxury">{t('booking.paymentLabel')}</label>
    <div className="grid grid-cols-2 gap-3">
      <button type="button" onClick={() => setPaymentMethod('card')} className={`vehicle-card flex items-center gap-2 ${paymentMethod === 'card' ? 'selected' : ''}`} data-testid="payment-card">
        <CreditCard size={18} className="text-[#C5A059]" />
        <span className="text-xs text-white/70">{t('booking.cardPayment')}</span>
      </button>
      <button type="button" onClick={() => setPaymentMethod('cash')} className={`vehicle-card flex items-center gap-2 ${paymentMethod === 'cash' ? 'selected' : ''}`} data-testid="payment-cash">
        <Banknote size={18} className="text-[#C5A059]" />
        <span className="text-xs text-white/70">{t('booking.cashPayment')}</span>
      </button>
    </div>
  </div>
);

// ============== HOOKS ==============

const useGoogleMapsLoader = () => {
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapsError, setMapsError] = useState(false);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      setMapsError(true);
      return;
    }

    if (window.google?.maps?.places) {
      setMapsLoaded(true);
      return;
    }

    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const checkInterval = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(checkInterval);
          setMapsLoaded(true);
        }
      }, 200);
      const timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
        setMapsError(true);
      }, 15000);
      return () => { clearInterval(checkInterval); clearTimeout(timeoutId); };
    }

    window.initGoogleMapsCallback = () => setMapsLoaded(true);

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async&callback=initGoogleMapsCallback`;
    script.async = true;
    script.defer = true;
    script.onerror = () => setMapsError(true);
    document.head.appendChild(script);
  }, []);

  return { mapsLoaded, mapsError };
};

const useAutocomplete = (inputRef, mapsLoaded, mapsError, setValue) => {
  const autocompleteRef = useRef(null);

  useEffect(() => {
    const input = inputRef.current;
    if (!mapsLoaded || mapsError || !window.google?.maps?.places || !input || autocompleteRef.current) return;

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: 'fr' }
      });
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place?.formatted_address) setValue(place.formatted_address);
      });
    } catch (e) {
      console.error('Autocomplete init error:', e);
    }
  }, [mapsLoaded, mapsError, inputRef, setValue]);
};

const useLoyaltyCheck = (email, phone) => {
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);

  useEffect(() => {
    if (!email && !phone) {
      setLoyaltyInfo(null);
      return;
    }

    const checkLoyalty = async () => {
      try {
        const response = await fetch(`${API_URL}/api/check-loyalty`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, phone })
        });
        if (response.ok) setLoyaltyInfo(await response.json());
      } catch (err) {
        console.log('Loyalty check error:', err);
      }
    };

    const debounce = setTimeout(checkLoyalty, 500);
    return () => clearTimeout(debounce);
  }, [email, phone]);

  return loyaltyInfo;
};

// ============== MAIN COMPONENT ==============

export const BookingForm = ({ lang, t }) => {
  // State
  const [mode, setMode] = useState('trip');
  const [pickupAddress, setPickupAddress] = useState('');
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [manualDistance, setManualDistance] = useState('');
  const [date, setDate] = useState(null);
  const [time, setTime] = useState('10:00');
  const [vehicle, setVehicle] = useState('model_y');
  const [hours, setHours] = useState(2);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [priceData, setPriceData] = useState(null);
  const [routeData, setRouteData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  
  // Revolut payment state
  const [showPayment, setShowPayment] = useState(false);
  const [orderToken, setOrderToken] = useState(null);
  const [revolutOrderId, setRevolutOrderId] = useState(null);
  const [depositAmount, setDepositAmount] = useState(0);

  // Refs
  const pickupRef = useRef(null);
  const dropoffRef = useRef(null);

  // Custom hooks
  const { mapsLoaded, mapsError } = useGoogleMapsLoader();
  const loyaltyInfo = useLoyaltyCheck(email, phone);

  // Initialize autocomplete
  useAutocomplete(pickupRef, mapsLoaded, mapsError, setPickupAddress);
  useAutocomplete(dropoffRef, mapsLoaded, mapsError, setDropoffAddress);

  // Memoized values
  const dateLocale = useMemo(() => lang === 'fr' ? fr : enUS, [lang]);
  const timeOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < 24; i++) {
      const hour = i.toString().padStart(2, '0');
      options.push(`${hour}:00`, `${hour}:30`);
    }
    return options.sort();
  }, []);

  const canCalculateTrip = useMemo(() => 
    Boolean(manualDistance) || (Boolean(pickupAddress) && Boolean(dropoffAddress)),
    [manualDistance, pickupAddress, dropoffAddress]
  );

  // Reset form data on mode change
  const handleModeChange = useCallback(() => {
    setPriceData(null);
    setRouteData(null);
  }, []);

  // Calculate distance from Google Maps
  const calculateDistanceFromMaps = useCallback(async () => {
    if (!pickupAddress || !dropoffAddress || mapsError || !window.google?.maps?.DistanceMatrixService) {
      return null;
    }
    try {
      const service = new window.google.maps.DistanceMatrixService();
      return new Promise((resolve) => {
        service.getDistanceMatrix({
          origins: [pickupAddress],
          destinations: [dropoffAddress],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC
        }, (response, status) => {
          if (status === 'OK' && response.rows[0]?.elements[0]?.status === 'OK') {
            const element = response.rows[0].elements[0];
            resolve({ distance_km: element.distance.value / 1000, duration_minutes: Math.ceil(element.duration.value / 60) });
          } else {
            resolve(null);
          }
        });
      });
    } catch (err) {
      return null;
    }
  }, [pickupAddress, dropoffAddress, mapsError]);

  // Calculate price
  const calculatePrice = useCallback(async () => {
    setIsCalculating(true);
    try {
      let distance = parseFloat(manualDistance);

      if (mode === 'trip' && !distance && pickupAddress && dropoffAddress) {
        const mapsResult = await calculateDistanceFromMaps();
        if (mapsResult) {
          distance = mapsResult.distance_km;
          setManualDistance(distance.toFixed(1));
          setRouteData(mapsResult);
        }
      }

      const requestBody = mode === 'hourly'
        ? { mode: 'hourly', hours, pickup_time: time, email, phone }
        : { mode: 'trip', distance_km: distance, pickup_time: time, email, phone };

      if (mode === 'trip' && (!distance || distance <= 0)) {
        setIsCalculating(false);
        return;
      }

      if (mode === 'trip' && !routeData) {
        setRouteData({ distance_km: distance, duration_minutes: Math.round(distance * 1.5) });
      }

      const response = await fetch(`${API_URL}/api/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      if (response.ok) setPriceData(await response.json());
    } catch (err) {
      console.error('Price calculation error:', err);
    } finally {
      setIsCalculating(false);
    }
  }, [mode, hours, time, manualDistance, email, phone, pickupAddress, dropoffAddress, calculateDistanceFromMaps, routeData]);

  // Auto-calculate for hourly mode
  useEffect(() => {
    if (mode === 'hourly') calculatePrice();
  }, [mode, hours, time, calculatePrice]);

  // Form validation
  const validateForm = useCallback(() => {
    if (!name || !phone || !date || !time) {
      alert(lang === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
      return false;
    }
    if (mode === 'trip' && (!pickupAddress || !dropoffAddress)) {
      alert(lang === 'fr' ? 'Veuillez entrer les adresses' : 'Please enter addresses');
      return false;
    }
    return true;
  }, [name, phone, date, time, mode, pickupAddress, dropoffAddress, lang]);

  // Submit handler
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const bookingData = {
        name, phone, email: email || null,
        pickup_date: format(date, 'yyyy-MM-dd'),
        pickup_time: time,
        pickup_address: pickupAddress || 'Mise à disposition',
        dropoff_address: dropoffAddress || 'Mise à disposition',
        vehicle, mode,
        distance_km: routeData?.distance_km || parseFloat(manualDistance) || null,
        duration_minutes: routeData?.duration_minutes || null,
        hours: mode === 'hourly' ? hours : null,
        payment_method: paymentMethod,
        language: lang
      };

      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      const data = await response.json();

      if (response.ok) {
        if (paymentMethod === 'card' && data.order_token) {
          // Show Revolut payment widget
          setOrderToken(data.order_token);
          setRevolutOrderId(data.revolut_order_id);
          setDepositAmount(data.booking?.deposit_amount || priceData?.deposit_amount || 0);
          setShowPayment(true);
        } else {
          resetForm();
          alert(lang === 'fr' 
            ? 'Réservation confirmée !\n\nVotre chauffeur vous contactera bientôt.' 
            : 'Booking confirmed!\n\nYour driver will contact you soon.');
        }
      } else {
        throw new Error(data.detail || 'Booking failed');
      }
    } catch (err) {
      console.error('Booking error:', err);
      alert(lang === 'fr' ? 'Erreur lors de la réservation. Veuillez réessayer.' : 'Booking error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, name, phone, email, date, time, pickupAddress, dropoffAddress, vehicle, mode, routeData, manualDistance, hours, paymentMethod, lang, priceData]);

  const handlePaymentSuccess = useCallback(() => {
    setShowPayment(false);
    // Redirect to success page with order ID
    window.location.href = `/booking-success?order_id=${revolutOrderId}`;
  }, [revolutOrderId]);

  const handlePaymentError = useCallback((err) => {
    console.error('Payment error:', err);
    setShowPayment(false);
    alert(lang === 'fr' ? 'Erreur de paiement. Veuillez réessayer.' : 'Payment error. Please try again.');
  }, [lang]);

  const handlePaymentCancel = useCallback(() => {
    setShowPayment(false);
  }, []);

  const resetForm = useCallback(() => {
    setName(''); setPhone(''); setEmail('');
    setPickupAddress(''); setDropoffAddress(''); setManualDistance('');
    setDate(null); setTime('10:00');
    setPriceData(null); setRouteData(null);
  }, []);

  return (
    <div className="glass-card p-6 lg:p-8 w-full max-w-lg mx-auto" data-testid="booking-form">
      <LoyaltyBadge lang={lang} isEligible={loyaltyInfo?.is_loyalty_eligible} />
      <ModeToggle mode={mode} setMode={setMode} t={t} onModeChange={handleModeChange} />

      <form onSubmit={handleSubmit} className="space-y-5">
        {mode === 'trip' && (
          <AddressFields
            pickupRef={pickupRef} dropoffRef={dropoffRef}
            pickupAddress={pickupAddress} setPickupAddress={setPickupAddress}
            dropoffAddress={dropoffAddress} setDropoffAddress={setDropoffAddress}
            manualDistance={manualDistance} setManualDistance={setManualDistance}
            lang={lang} t={t} isCalculating={isCalculating}
            onCalculate={calculatePrice} canCalculate={canCalculateTrip}
          />
        )}

        {mode === 'hourly' && <HoursSelector hours={hours} setHours={setHours} t={t} />}

        <PriceDisplay routeData={routeData} priceData={priceData} lang={lang} t={t} />

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-luxury flex items-center gap-2"><CalendarIcon size={12} />{t('booking.dateLabel')}</label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button type="button" className="input-luxury text-left flex items-center justify-between" data-testid="date-picker-trigger">
                  {date ? format(date, 'dd/MM/yyyy', { locale: dateLocale }) : t('booking.selectDate')}
                  <CalendarIcon size={16} className="text-white/40" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#111111] border-white/10" align="start">
                <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                  disabled={(d) => d < new Date()} locale={dateLocale} className="bg-[#111111]" />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label className="label-luxury flex items-center gap-2"><Clock size={12} />{t('booking.timeLabel')}</label>
            <select value={time} onChange={(e) => setTime(e.target.value)} className="select-luxury" data-testid="time-select">
              {timeOptions.map(opt => <option key={`time-${opt}`} value={opt}>{opt}</option>)}
            </select>
          </div>
        </div>

        <VehicleSelector vehicle={vehicle} setVehicle={setVehicle} t={t} />

        {/* Name & Phone */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-luxury flex items-center gap-2"><User size={12} />{t('booking.nameLabel')}</label>
            <input type="text" className="input-luxury" placeholder={t('booking.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} required data-testid="name-input" />
          </div>
          <div>
            <label className="label-luxury flex items-center gap-2"><Phone size={12} />{t('booking.phoneLabel')}</label>
            <input type="tel" className="input-luxury" placeholder={t('booking.phonePlaceholder')} value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="phone-input" />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="label-luxury flex items-center gap-2"><Mail size={12} />{lang === 'fr' ? 'Email (pour confirmation)' : 'Email (for confirmation)'}</label>
          <input type="email" className="input-luxury" placeholder={lang === 'fr' ? 'votre@email.com' : 'your@email.com'} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-input" />
          {loyaltyInfo && !loyaltyInfo.is_loyalty_eligible && loyaltyInfo.bookings_until_loyalty > 0 && (
            <p className="text-white/40 text-xs mt-1">{lang === 'fr' ? `Encore ${loyaltyInfo.bookings_until_loyalty} trajet(s) pour -10%` : `${loyaltyInfo.bookings_until_loyalty} more trip(s) for -10%`}</p>
          )}
        </div>

        <PaymentMethodSelector paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} t={t} />

        {/* Submit */}
        <button type="submit" disabled={isSubmitting || !priceData} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="submit-booking-btn">
          {isSubmitting ? <><Loader2 className="animate-spin" size={18} />{t('booking.processing')}</> : t('booking.bookBtn')}
        </button>
      </form>

      {/* Revolut Payment Modal */}
      {showPayment && orderToken && (
        <RevolutPayment
          orderToken={orderToken}
          depositAmount={depositAmount}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onCancel={handlePaymentCancel}
          lang={lang}
        />
      )}
    </div>
  );
};

export default BookingForm;
